import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { Inventory2Outlined } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlags';
import { LockedFeature } from '../planLock/LockedFeature';
import { usePlanSurface } from '../planLock/usePlanSurface';
import { useToast } from '../../contexts/ToastContext';
import { useUnsavedChangesSource } from '../appUpdate/unsavedChanges';
import { InlineMessage, LoadingState, NumberField, SectionCard, parseNumberInput } from '../ui';
import { REORDER_SETTINGS_QUERY_KEY, REORDER_SUGGESTIONS_QUERY_KEY, isReorderPlanLocked, reorderApi } from './reorderApi';
import { TrialFeatureTitle } from '../trial/TrialFeatureBadge';

const MAX_LEAD = 365;
const MAX_SAFETY = 90;

/**
 * Jordan sets the inputs the reorder suggestions rest on (flag
 * `predictive_reorder`, plan `advancedAnalytics`; MOT-149): a default lead
 * time, safety-stock days and a lead time per brand the store sells. One Save
 * for the card, enabled only while something changed. The Shopify stock
 * switch (MOT-395) opts the store in to a sampled Shopify inventory read for
 * products it has not counted; its own counts always win.
 */
export const ReorderSettingsCard: React.FC = () => {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { enabled } = useOrgFeatureFlag('predictive_reorder');
  const surface = usePlanSurface({ feature: 'advancedAnalytics', orgFlag: 'predictive_reorder' });
  const [lead, setLead] = useState('');
  const [safety, setSafety] = useState('');
  const [brandLead, setBrandLead] = useState<Record<string, string>>({});
  const [useShopify, setUseShopify] = useState(false);

  const query = useQuery({
    queryKey: REORDER_SETTINGS_QUERY_KEY,
    queryFn: () => reorderApi.getSettings(t('store.reorder.loadError')),
    enabled: enabled && surface.mode === 'open',
    retry: false,
  });
  const data = query.data;

  useEffect(() => {
    if (!data) return;
    setLead(String(data.settings.defaultLeadTimeDays));
    setSafety(String(data.settings.safetyStockDays));
    const rows: Record<string, string> = {};
    for (const [brandId, days] of Object.entries(data.settings.brandLeadTimeDays)) rows[brandId] = String(days);
    setBrandLead(rows);
    setUseShopify(data.settings.useShopifyOnHand === true);
  }, [data]);

  const leadValue = parseNumberInput(lead);
  const safetyValue = parseNumberInput(safety);
  const invalid =
    leadValue == null || leadValue < 0 || leadValue > MAX_LEAD || safetyValue == null || safetyValue < 0 || safetyValue > MAX_SAFETY ||
    Object.values(brandLead).some((v) => {
      const n = parseNumberInput(v);
      return v.trim() !== '' && (n == null || n < 0 || n > MAX_LEAD);
    });

  const dirty = useMemo(() => {
    if (!data) return false;
    if (leadValue !== data.settings.defaultLeadTimeDays || safetyValue !== data.settings.safetyStockDays) return true;
    if (useShopify !== (data.settings.useShopifyOnHand === true)) return true;
    const stored = data.settings.brandLeadTimeDays;
    const keys = Array.from(new Set([...Object.keys(stored), ...Object.keys(brandLead)]));
    for (const key of keys) {
      const typed = parseNumberInput(brandLead[key] ?? '');
      if ((stored[key] ?? null) !== typed) return true;
    }
    return false;
  }, [data, leadValue, safetyValue, brandLead, useShopify]);

  const save = useMutation({
    mutationFn: async () => {
      const brandLeadTimeDays: Record<string, number | null> = {};
      for (const [brandId, value] of Object.entries(brandLead)) {
        const n = parseNumberInput(value);
        if (n != null) brandLeadTimeDays[brandId] = Math.round(n);
      }
      return reorderApi.saveSettings(
        { defaultLeadTimeDays: Math.round(leadValue ?? 0), safetyStockDays: Math.round(safetyValue ?? 0), brandLeadTimeDays, useShopifyOnHand: useShopify },
        t('store.reorder.saveSettingsError'),
      );
    },
    onSuccess: (payload) => {
      queryClient.setQueryData(REORDER_SETTINGS_QUERY_KEY, payload);
      queryClient.invalidateQueries({ queryKey: REORDER_SUGGESTIONS_QUERY_KEY });
      showToast(t('store.reorder.settingsSaved'), 'success');
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  const discard = () => {
    if (!data) return;
    setLead(String(data.settings.defaultLeadTimeDays));
    setSafety(String(data.settings.safetyStockDays));
    const rows: Record<string, string> = {};
    for (const [brandId, days] of Object.entries(data.settings.brandLeadTimeDays)) rows[brandId] = String(days);
    setBrandLead(rows);
    setUseShopify(data.settings.useShopifyOnHand === true);
  };
  useUnsavedChangesSource({ dirty, save: () => save.mutateAsync(), discard });

  if (surface.mode === 'locked') {
    return (
      <SectionCard title={t('store.reorder.settings.title')} leading={<Inventory2Outlined color="primary" />}>
        <LockedFeature variant="inline" planName={surface.planName} title={t('store.reorder.settings.title')} />
      </SectionCard>
    );
  }
  if (!enabled || surface.mode !== 'open') return null;
  // The plan gate's 403: the card has nothing to set until the plan includes Advanced analytics.
  if (query.isError && isReorderPlanLocked(query.error)) return null;

  return (
    <SectionCard title={<TrialFeatureTitle title={t('store.reorder.settings.title')} feature="advancedAnalytics" />} leading={<Inventory2Outlined color="primary" />}>
      {query.isLoading ? (
        <LoadingState />
      ) : query.isError || !data ? (
        <InlineMessage severity="error">{t('store.reorder.loadError')}</InlineMessage>
      ) : (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('store.reorder.settings.intro')}
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <NumberField
              label={t('store.reorder.settings.defaultLead')}
              value={lead}
              onChange={setLead}
              min={0}
              max={MAX_LEAD}
              integer
              allowEmpty={false}
              adornment={t('store.reorder.settings.days')}
              helperText={t('store.reorder.settings.defaultLeadHelp')}
              sx={{ width: 220 }}
            />
            <NumberField
              label={t('store.reorder.settings.safety')}
              value={safety}
              onChange={setSafety}
              min={0}
              max={MAX_SAFETY}
              integer
              allowEmpty={false}
              adornment={t('store.reorder.settings.days')}
              helperText={t('store.reorder.settings.safetyHelp')}
              sx={{ width: 220 }}
            />
          </Stack>
          {data.brands.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('store.reorder.settings.brandLead')}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                {t('store.reorder.settings.brandLeadHelp', { days: data.settings.defaultLeadTimeDays })}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(auto-fill, minmax(220px, 1fr))' }, gap: 1.5 }}>
                {data.brands.map((brand) => (
                  <NumberField
                    key={brand.brandId}
                    label={brand.brandName}
                    value={brandLead[brand.brandId] ?? ''}
                    onChange={(value) => setBrandLead((rows) => ({ ...rows, [brand.brandId]: value }))}
                    min={0}
                    max={MAX_LEAD}
                    integer
                    adornment={t('store.reorder.settings.days')}
                    placeholder={t('store.reorder.settings.usesDefault', { days: data.settings.defaultLeadTimeDays })}
                  />
                ))}
              </Box>
            </Box>
          )}
          <Box>
            <FormControlLabel
              control={<Switch checked={useShopify} onChange={(e) => setUseShopify(e.target.checked)} />}
              label={t('store.reorder.settings.shopifyOnHand')}
            />
            <Typography variant="caption" color="text.secondary" display="block">
              {t('store.reorder.settings.shopifyOnHandHelp')}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" size="small" disabled={!dirty || invalid || save.isPending} onClick={() => save.mutate()}>
              {t('common.save')}
            </Button>
            <Button size="small" disabled={!dirty || save.isPending} onClick={discard}>
              {t('common.cancel')}
            </Button>
          </Stack>
        </Stack>
      )}
    </SectionCard>
  );
};

export default ReorderSettingsCard;
